import React, { useState, useEffect, useMemo, useRef } from "react";

import OperationsHub from "../OperationsHub";
import Loading from "../Loading";
import ContentHeader from "../ContentHeader";
import NoContent from "../NoContent";
import Modal from "../Modal";
import Paginator from "../shared/Paginator.jsx";

import data from "../../../../_data/operations-hub.json";
import { usePagedList } from "../../hooks/usePagedList.js";

export default function ExpirationTracker() {
  const [projects, setProjects] = useState([]);
  const [filterSelected, setFilterSelected] = useState(null);

  const itemsPerPage = 12;

  useEffect(() => {
    let visibleProjects = [];

    if (data && data.projects) {
      visibleProjects = data.projects.filter((p) => p.isVisible);
    }

    setProjects(visibleProjects);
  }, []);

  const expiringProjects = useMemo(
    () =>
      projects?.filter((p) => {
        const count = countAffectedAccesses(p, "expiring");

        return count > 0;
      }),
    [projects],
  );

  const expiredProjects = useMemo(
    () =>
      projects?.filter((p) => {
        const count = countAffectedAccesses(p, "expired");

        return count > 0;
      }),
    [projects],
  );

  const filteredData = useMemo(
    () =>
      projects.filter((item) => {
        const count = countAffectedAccesses(item, filterSelected);

        return count > 0;
      }),
    [projects, filterSelected],
  );

  const { currentPage, setCurrentPage, pageItems, pageCount, pagesToDisplay } = usePagedList(
    filteredData,
    itemsPerPage,
  );

  if (!projects) return <Loading />;

  return (
    <>
      <OperationsHub />

      <ContentHeader
        title="Expiration Tracker"
        caption="A view for tracking the lifespan of project tokens and resource accesses."
      />

      <div className="expiration-tracker">
        <span className="expiration-header">
          <div
            style={{
              height: "6rem",
              width: "0.375rem",
              background: "linear-gradient(180deg, #E1002A 0%, #5237ac 100%)",
            }}
          />

          <div className="expiration-header-text">
            <h1>
              {expiringProjects.length + expiredProjects.length} out of {projects.length}
            </h1>
            <p>projects affected.</p>
          </div>
        </span>

        <div className="expiry-filter">
          <button
            className="filter-button"
            onClick={() =>
              setFilterSelected(() => {
                setCurrentPage(0);
                return "expiring";
              })
            }
          >
            <h3 className="filter-button-number" style={{ backgroundColor: "orange" }}>
              {expiringProjects.length}
            </h3>
            <h3 className="filter-button-paragraph">Projects with accesses near expiration</h3>
          </button>
          <button
            className="filter-button"
            onClick={() =>
              setFilterSelected(() => {
                setCurrentPage(0);
                return "expired";
              })
            }
          >
            <h3 className="filter-button-number" style={{ backgroundColor: "darkred" }}>
              {expiredProjects.length}
            </h3>
            <h3 className="filter-button-paragraph">Projects with expired accesses</h3>
          </button>
        </div>

        <ProjectGrid projects={pageItems} filterBy={filterSelected} />

        <Paginator
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageCount={pageCount}
          pagesToDisplay={pagesToDisplay}
        />
      </div>
    </>
  );
}

function ProjectGrid({ projects = new Set(), filterBy }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const selectedProject = useRef(null);

  if (!projects || projects.length <= 0)
    return <NoContent message="All clear! No projects affected." />;

  const createAccessLifespanChips = (project) => {
    const accessPercentages = [];
    const accessChips = {
      chipElements: [],
      chipLegend: {
        EXPIRED: false,
        NEAR_EXPIRATION: false,
      },
    };

    for (const access of project.accesses) {
      const expiryDate = new Date(access.expiresAt);
      const grantedDate = new Date(access.grantedOn);

      const totalAccessDays =
        (expiryDate.getTime() - grantedDate.getTime()) / (1000 * 60 * 60 * 24);
      const remainingDays = (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      const remainingAccessPercentage = (remainingDays / totalAccessDays) * 100;

      accessPercentages.push(remainingAccessPercentage);
    }

    accessChips.chipElements = accessPercentages
      .sort((current, next) => next - current)
      .map((percentage, index) => {
        if (percentage > 50) {
          return (
            <li key={`lifespanChip-${index}`}>
              <div className="chip-style optimum" />
            </li>
          );
        } else if (percentage <= 50 && percentage >= 0) {
          accessChips.chipLegend.NEAR_EXPIRATION = true;

          return (
            <li key={`lifespanChip-${index}`}>
              <div className="chip-style near-expiration" />
            </li>
          );
        }

        accessChips.chipLegend.EXPIRED = true;

        return (
          <li key={`lifespanChip-${index}`}>
            <div className="chip-style expired" />
          </li>
        );
      });

    return accessChips;
  };

  const mappedProjects = projects.map((proj, index) => {
    const accessChips = createAccessLifespanChips(proj);
    const accessExpiryCount = countAffectedAccesses(proj, filterBy);
    const totalAccesses = proj.accesses.length;

    return (
      <li key={`project-${index}`} className="grid-item">
        <button
          onClick={() => {
            selectedProject.current = proj;
            setIsModalVisible(true);
          }}
        >
          <h3>{proj.title}</h3>

          {!filterBy ? (
            <p>
              <span>
                {accessExpiryCount}/{totalAccesses}
              </span>{" "}
              accesses affected.
            </p>
          ) : (
            <></>
          )}
          {filterBy?.valueOf() == "expiring" ? (
            <p>
              <span>
                {accessExpiryCount}/{totalAccesses}
              </span>{" "}
              near expiration
            </p>
          ) : (
            <></>
          )}
          {filterBy?.valueOf() == "expired" ? (
            <p>
              <span>
                {accessExpiryCount}/{totalAccesses}
              </span>{" "}
              expired
            </p>
          ) : (
            <></>
          )}

          <ul className="chip-bar">{accessChips.chipElements}</ul>

          <ul className="chip-legend">
            {accessChips.chipLegend.NEAR_EXPIRATION ? (
              <li className="chip-style near-expiration">
                <p>Near Expiration</p>
              </li>
            ) : (
              <></>
            )}
            {accessChips.chipLegend.EXPIRED ? (
              <li className="chip-style expired">
                <p>Expired</p>
              </li>
            ) : (
              <></>
            )}
          </ul>
        </button>
      </li>
    );
  });

  return (
    <>
      <ul className="project-grid">{mappedProjects}</ul>

      {isModalVisible && (
        <Modal onClose={() => setIsModalVisible(false)}>
          <ExpiryModalContent project={selectedProject.current} />
        </Modal>
      )}
    </>
  );
}

function ExpiryModalContent({ project }) {
  const mappedAccesses = project.accesses.map((access, index) => {
    const accessName = access.name;
    const grantedDate = new Date(access.grantedOn);
    const expiryDate = new Date(access.expiresAt);

    const totalAccessDays = (expiryDate.getTime() - grantedDate.getTime()) / (1000 * 60 * 60 * 24);
    const dateDifference = (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    const expiryMeterVal = (dateDifference / totalAccessDays) * 100;

    const grantedDateStr = grantedDate.toLocaleDateString("default", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
    const expiryDateStr = expiryDate.toLocaleDateString("default", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });

    return (
      <li key={`projects-${index}`} className="access-item">
        <label>{accessName}</label>
        <p className="access-countdown" style={{ color: dateDifference < 1 ? "red" : "" }}>
          {dateDifference >= 1
            ? `Expires in ${Math.floor(dateDifference)} ${dateDifference > 1 ? "days" : "day"}`
            : "Access has expired. Please renew immediately."}
        </p>
        <span className="access-dates">
          <p>Granted: {grantedDateStr}</p>
          <p>Expires: {expiryDateStr}</p>
        </span>
        <meter
          min={0}
          max={100}
          optimum={30}
          high={50}
          value={expiryMeterVal}
          className="access-bar"
        />
      </li>
    );
  });

  return (
    <>
      <h1>{project.title}</h1>

      <ul className="access-grid">{mappedAccesses}</ul>
    </>
  );
}

function countAffectedAccesses(project, filterBy = "") {
  const accesses = project.accesses;

  if (!accesses) return 0;

  let count = 0;

  for (const access of accesses) {
    const expiryDate = new Date(access.expiresAt);
    const grantDate = new Date(access.grantedOn);

    const totalAccessDays = (expiryDate.getTime() - grantDate.getTime()) / (1000 * 60 * 60 * 24);
    const remainingDays = (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    const remainingAccessPercentage = (remainingDays / totalAccessDays) * 100;

    switch (filterBy) {
      case "expiring":
        if (remainingDays <= 30 && remainingDays > 0 && remainingAccessPercentage < 35) count += 1;
        break;

      case "expired":
        if (remainingDays < 0) count += 1;
        break;

      default: // No filter
        if (remainingDays < 30) count += 1;
        break;
    }
  }

  return count;
}
